import { useEffect, useRef, useState } from 'react';
import {
  heroSignals,
  useScrollProgress,
  mapRange,
  type AnchorLabel,
} from '../heroSignals';

/**
 * Orbiting image cards — flat ellipse around the model center.
 * ─────────────────────────────────────────────────────────────
 * Each card owns a base angle. The ring rotates continuously (drift) plus
 * an extra rotation scrubbed by scroll.
 *
 *   x = cx + r  * cos(angle)
 *   y = cy + r  * sin(angle) * ELLIPSE_RATIO
 *
 * Layer & occlusion rules (this is the core of what makes it feel like a
 * real orbit around the model instead of two static floating panels):
 *   • sin(angle) <= 0  →  card is on the TOP half of the ellipse.
 *                         Card sits ABOVE the model (z-index > model),
 *                         opacity = 1. Card can occlude the model.
 *   • sin(angle)  > 0  →  card is on the BOTTOM half.
 *                         Card sits BEHIND the model (z-index < model)
 *                         and its opacity fades to 0 as it approaches the
 *                         far-back (angle = π/2, i.e. directly behind).
 *                         When it swings back toward the sides, opacity
 *                         eases back to 1 before z-index flips again — so
 *                         no popping.
 *
 * The z-index flip happens at sin(angle) = 0, where the card is at the
 * horizontal extreme of the ellipse (far left or far right). At that point
 * it doesn't visually overlap the centered model, so no flash is visible.
 *
 * Text pins (anchor dots) still project from ProductShowcase per-frame.
 */

type FloatCard = {
  id: string;
  src: string;
  width: number;
  aspect: number; // height / width
  order: number;
  linkedAnchorId: string;
  baseAngle: number; // radians, position on the ellipse
  detail: {
    eyebrow: string;
    title: string;
    body: string;
    spec: { label: string; value: string }[];
  };
};

// Four cards evenly spread on the ring so at any moment 1 is roughly
// hidden at the back, 2 are on the sides, 1 is at the front.
const FLOAT_CARDS: FloatCard[] = [
  {
    id: 'axiom-keys',
    src:
      'https://readdy.ai/api/search-image?query=Extreme%20macro%20close-up%20of%20a%20cyan%20blue%20PLAY%20button%20on%20a%20premium%20matte%20black%20portable%20audio%20device%2C%20precisely%20engineered%20tactile%20micro-switch%2C%20sharp%20chamfered%20edges%2C%20subtle%20teal%20rim%20light%20from%20left%20and%20soft%20amber%20fill%20from%20right%2C%20clean%20deep%20charcoal%20navy%20background%2C%20industrial%20design%20studio%20photography%2C%20high%20detail%20texture%20of%20anodized%20metal%20and%20soft-touch%20plastic%2C%20cinematic%20mood&width=600&height=750&seq=axiom-keys-macro-01&orientation=portrait',
    width: 180,
    aspect: 1.25,
    order: 0,
    linkedAnchorId: 'play',
    baseAngle: 0, // right side of ellipse
    detail: {
      eyebrow: 'CONTROL · PLAY',
      title: 'Tactile Micro-switch',
      body: 'Cyan-anodized aluminum keycap with a 0.35mm tactile bump. Engineered for a crisp 120-gram actuation and rated for two million cycles of daily use.',
      spec: [
        { label: 'ACTUATION', value: '120 g' },
        { label: 'TRAVEL', value: '0.7 mm' },
        { label: 'RATED CYCLES', value: '2,000,000' },
      ],
    },
  },
  {
    id: 'axiom-grille',
    src:
      'https://readdy.ai/api/search-image?query=Extreme%20macro%20close-up%20of%20a%20precision%20perforated%20speaker%20grille%20mesh%20on%20a%20premium%20portable%20audio%20device%2C%20perfectly%20aligned%20round%20holes%20pattern%20on%20brushed%20titanium%20surface%2C%20soft%20teal%20rim%20light%20on%20one%20side%20and%20warm%20amber%20fill%20on%20the%20other%2C%20clean%20deep%20charcoal%20navy%20background%2C%20industrial%20design%20studio%20photography%2C%20shallow%20depth%20of%20field%2C%20cinematic%20mood&width=800&height=600&seq=axiom-grille-macro-02&orientation=landscape',
    width: 200,
    aspect: 0.72,
    order: 1,
    linkedAnchorId: 'driver',
    baseAngle: -Math.PI / 2, // TOP of ellipse — starts on top of the model
    detail: {
      eyebrow: 'COMPONENT · DRIVER',
      title: '40mm Beryllium Driver',
      body: 'A precision-perforated titanium grille protects a 40mm beryllium dome driver. Tuned in-house for a wide 8Hz–38kHz response with sub-1% distortion at reference volume.',
      spec: [
        { label: 'DIAPHRAGM', value: 'Beryllium' },
        { label: 'FREQ. RESP.', value: '8–38 kHz' },
        { label: 'THD', value: '< 1 %' },
      ],
    },
  },
  {
    id: 'axiom-antenna',
    src:
      'https://readdy.ai/api/search-image?query=Extreme%20macro%20close-up%20of%20a%20chrome%20telescopic%20antenna%20root%20base%20joint%20on%20a%20premium%20matte%20black%20portable%20audio%20radio%20device%2C%20precision%20machined%20metal%20hinge%20with%20fine%20knurling%20pattern%2C%20subtle%20teal%20rim%20light%20from%20above%20and%20soft%20amber%20fill%20from%20side%2C%20clean%20deep%20charcoal%20navy%20background%2C%20industrial%20design%20studio%20photography%2C%20high%20detail%20texture%20of%20brushed%20chrome%20and%20anodized%20metal%2C%20cinematic%20editorial%20mood&width=650&height=750&seq=axiom-antenna-macro-03&orientation=portrait',
    width: 175,
    aspect: 1.15,
    order: 2,
    linkedAnchorId: 'antenna',
    baseAngle: Math.PI, // left side of ellipse
    detail: {
      eyebrow: 'ARRAY · ANTENNA',
      title: 'Wideband RF Array',
      body: 'A machined-chrome telescopic antenna paired with an internal PCB array covers FM, DAB+ and shortwave in a single hinge. Anodized knurling gives grip while remaining discreet against the frame.',
      spec: [
        { label: 'BANDS', value: 'FM / DAB+ / SW' },
        { label: 'LENGTH', value: '186 mm' },
        { label: 'HINGE', value: '6-axis chrome' },
      ],
    },
  },
  {
    id: 'axiom-frame',
    src:
      'https://readdy.ai/api/search-image?query=Extreme%20macro%20close-up%20of%20a%20precision%20machined%20titanium%20frame%20edge%20on%20a%20premium%20portable%20audio%20device%2C%20fine%20brushed%20metal%20texture%20with%20precise%20chamfered%20bevel%2C%20subtle%20teal%20rim%20light%20from%20upper%20left%20and%20warm%20amber%20fill%20from%20lower%20right%2C%20clean%20deep%20charcoal%20navy%20background%2C%20industrial%20design%20studio%20photography%2C%20high%20detail%20texture%20of%20brushed%20titanium%20surface%2C%20cinematic%20editorial%20mood&width=650&height=800&seq=axiom-frame-macro-04&orientation=portrait',
    width: 185,
    aspect: 1.25,
    order: 3,
    linkedAnchorId: 'cell',
    baseAngle: Math.PI / 2, // BOTTOM of ellipse — starts hidden behind model
    detail: {
      eyebrow: 'MODULE · CELL',
      title: 'Titanium Chassis · 5000mAh',
      body: 'A single-piece titanium chassis houses a 5000mAh lithium cell with USB-C PD 3.0 fast-charging. Full charge in 42 minutes yields up to 26 hours of continuous playback at 60% volume.',
      spec: [
        { label: 'CAPACITY', value: '5000 mAh' },
        { label: 'PLAYBACK', value: '26 h' },
        { label: 'CHARGE', value: 'USB-C PD 3.0' },
      ],
    },
  },
];

// ── Orbit geometry ──────────────────────────────────────────────
const ORBIT_CENTER_X_VW = 50;
const ORBIT_CENTER_Y_VH = 50;
const ORBIT_RADIUS_VW = 32; // horizontal radius — cards reach ~18vw / ~82vw at the sides
const ELLIPSE_RATIO = 0.42; // vertical squish — makes the orbit read as a flat plane
const DRIFT_SPEED = 0.14; // rad/sec — continuous slow rotation
const SCROLL_BOOST_TOTAL = Math.PI * 1.2; // extra rotation across label stage
const ORBIT_START = 0.45;
const ORBIT_END = 0.62;

// Labels layer fades out just before Chapter 002 model migration begins.
const CH2_FADE_START = 0.56;
const CH2_FADE_END = 0.64;

// Model itself has z-index 10 (see ProductShowcase). Cards on the top half
// of the orbit paint above (11+), cards on the bottom half paint below (5).
const Z_MODEL = 10;
const Z_FRONT = 18;
const Z_BACK = 5;

export default function AnchorLabels() {
  const [anchors, setAnchors] = useState<AnchorLabel[]>([]);
  const scroll = useScrollProgress();
  const [hovered, setHovered] = useState<{
    kind: 'dot' | 'card';
    anchorId: string;
    cardX?: number;
    cardY?: number;
  } | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  const smoothVisRef = useRef<Record<string, number>>({});
  const startTimeRef = useRef<number>(performance.now());
  const [, setTick] = useState(0);

  // Continuous rAF loop — always ticks so the orbit keeps drifting even
  // when the user isn't scrolling. Also folds in the anchor sync + smooth
  // visibility work.
  useEffect(() => {
    let rafId = 0;
    let lastVersion = -1;
    const tick = () => {
      if (heroSignals.anchorsVersion !== lastVersion) {
        lastVersion = heroSignals.anchorsVersion;
        setAnchors(heroSignals.anchors.slice());
      }
      const svs = smoothVisRef.current;
      const seen = new Set<string>();
      for (const a of heroSignals.anchors) {
        seen.add(a.id);
        const target = a.onScreen ? 1 : 0;
        const cur = svs[a.id] ?? 0;
        svs[a.id] = cur + (target - cur) * 0.12;
      }
      for (const k of Object.keys(svs)) {
        if (!seen.has(k)) {
          svs[k] = svs[k] + (0 - svs[k]) * 0.12;
        }
      }
      setTick((n) => (n + 1) % 100000);
      rafId = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(rafId);
  }, []);

  const stageT = mapRange(scroll, 0.45, 0.58);
  const ch2FadeOut = 1 - mapRange(scroll, CH2_FADE_START, CH2_FADE_END);
  if (stageT <= 0 || ch2FadeOut <= 0.01) return null;

  // Continuous orbit rotation (autoplay) + scroll-driven boost.
  const orbitTimeSec =
    (performance.now() - startTimeRef.current) / 1000;
  const scrollBoost =
    mapRange(scroll, ORBIT_START, ORBIT_END) * SCROLL_BOOST_TOTAL;
  const orbitRot = orbitTimeSec * DRIFT_SPEED + scrollBoost;

  const cx = (ORBIT_CENTER_X_VW / 100) * window.innerWidth;
  const cy = (ORBIT_CENTER_Y_VH / 100) * window.innerHeight;
  const rx = (ORBIT_RADIUS_VW / 100) * window.innerWidth;

  const anchorMap: Record<string, AnchorLabel> = {};
  for (const a of anchors) anchorMap[a.id] = a;

  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        fontFamily:
          'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
        // No z-index — we deliberately do NOT create a stacking context here
        // so children can be either above (>10) or below (<10) the 3D model.
        opacity: ch2FadeOut,
        transition: 'opacity 120ms linear',
      }}
    >
      {/* Anchor dots — always painted on top of everything. */}
      {anchors.map((a) => {
        const t = smoothVisRef.current[a.id] ?? 0;
        if (t <= 0.01) return null;
        const isHovered =
          hovered !== null && hovered.kind === 'dot' && hovered.anchorId === a.id;
        return (
          <AnchorDot
            key={a.id}
            anchor={a}
            t={t}
            hovered={isHovered}
            onHover={(hoverIn) =>
              setHovered(hoverIn ? { kind: 'dot', anchorId: a.id } : null)
            }
          />
        );
      })}

      {/* Orbiting image cards — flat ellipse around the model center. */}
      {FLOAT_CARDS.map((card) => {
        const start = 0.48 + card.order * 0.02;
        const end = start + 0.06;
        const fadeT = mapRange(scroll, start, end);
        if (fadeT <= 0) return null;

        const angle = card.baseAngle + orbitRot;
        const s = Math.sin(angle);
        const c = Math.cos(angle);

        const x = cx + rx * c;
        const y = cy + rx * s * ELLIPSE_RATIO;

        // Layering rule.
        // sin(angle) <= 0  → top half of ellipse → in front of model.
        // sin(angle)  > 0  → bottom half → behind model.
        const isFront = s <= 0;
        const paintZ = isFront ? Z_FRONT : Z_BACK;

        // Opacity fade for the back half.
        // s = 0    (side)      → 1
        // s = 0.5  (~30° down) → ~0.62
        // s = 0.85 (near back) → ~0.09
        // s = 1    (dead back) → 0
        const bottomFade = Math.max(0, s);
        const orbitOpacity = 1 - Math.pow(bottomFade, 1.4);

        // Tiny scale cue — cards read as "closer" when at top, "farther"
        // when passing the sides. Very subtle (~5%). Does not compete
        // with opacity for depth reading.
        const scaleCue = 0.96 + (1 - s) * 0.04; // s=-1 → 1.04, s=1 → 0.96

        return (
          <OrbitingImageCard
            key={card.id}
            card={card}
            fadeT={fadeT}
            orbitOpacity={orbitOpacity}
            x={x}
            y={y}
            paintZ={paintZ}
            scaleCue={scaleCue}
            isFront={isFront}
            selected={selectedCardId === card.id}
            hovered={
              hovered !== null &&
              hovered.kind === 'card' &&
              hovered.anchorId === card.linkedAnchorId
            }
            onHover={(hoverIn) => {
              if (hoverIn) {
                setHovered({
                  kind: 'card',
                  anchorId: card.linkedAnchorId,
                  cardX: x,
                  cardY: y,
                });
              } else {
                setHovered((prev) =>
                  prev && prev.kind === 'card' && prev.anchorId === card.linkedAnchorId
                    ? null
                    : prev
                );
              }
            }}
            onClick={() => {
              setSelectedCardId((prev) => (prev === card.id ? null : card.id));
            }}
          />
        );
      })}

      {hovered && hovered.kind === 'card' && anchorMap[hovered.anchorId] && (
        <CardHoverLabel
          x={hovered.cardX ?? 0}
          y={hovered.cardY ?? 0}
          anchor={anchorMap[hovered.anchorId]}
        />
      )}

      {selectedCardId &&
        (() => {
          const selected = FLOAT_CARDS.find((c) => c.id === selectedCardId);
          if (!selected) return null;
          return (
            <CardDetailPanel
              detail={selected.detail}
              onClose={() => setSelectedCardId(null)}
            />
          );
        })()}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Anchor dot with hover-triggered label
// ─────────────────────────────────────────────────────────────
function AnchorDot({
  anchor,
  t,
  hovered,
  onHover,
}: {
  anchor: AnchorLabel;
  t: number;
  hovered: boolean;
  onHover: (hoverIn: boolean) => void;
}) {
  const dotColor = '#7AA8EE';
  const isLeft = anchor.side === 'left';

  return (
    <div
      className="absolute"
      style={{
        left: `${anchor.x}px`,
        top: `${anchor.y}px`,
        transform: 'translate(-50%, -50%)',
        opacity: t,
        transition: 'opacity 160ms linear',
        willChange: 'opacity',
        zIndex: 25,
      }}
    >
      <div
        className="absolute rounded-full pointer-events-auto cursor-pointer"
        style={{
          width: '28px',
          height: '28px',
          left: '-14px',
          top: '-14px',
        }}
        onPointerEnter={() => onHover(true)}
        onPointerLeave={() => onHover(false)}
      />
      <span
        className="absolute block pointer-events-none"
        style={{
          left: '-4px',
          top: '-4px',
          width: '8px',
          height: '8px',
        }}
      >
        <span
          className="absolute inset-0 rounded-full"
          style={{
            backgroundColor: dotColor,
            boxShadow: `0 0 12px ${dotColor}80`,
          }}
        />
        <span
          className="absolute inset-0 rounded-full"
          style={{
            backgroundColor: dotColor,
            animation: 'axiomAnchorPulse 2.2s ease-out infinite',
          }}
        />
      </span>

      <div
        className="absolute pointer-events-none"
        style={{
          top: '0px',
          left: isLeft ? undefined : '0px',
          right: isLeft ? '0px' : undefined,
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateY(0)' : 'translateY(5px)',
          transition:
            'opacity 240ms ease, transform 240ms cubic-bezier(0.22,1,0.36,1)',
          willChange: 'opacity, transform',
        }}
      >
        <span
          className="absolute block"
          style={{
            top: '0px',
            left: isLeft ? '-100px' : '0px',
            width: '100px',
            height: '1px',
            background: isLeft
              ? `linear-gradient(to left, ${dotColor}CC 0%, ${dotColor}20 100%)`
              : `linear-gradient(to right, ${dotColor}CC 0%, ${dotColor}20 100%)`,
          }}
        />
        <div
          className="absolute whitespace-nowrap"
          style={{
            top: '-14px',
            left: isLeft ? undefined : '112px',
            right: isLeft ? '112px' : undefined,
            textAlign: isLeft ? 'right' : 'left',
            padding: '8px 12px',
            background: 'rgba(20, 23, 31, 0.82)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '4px',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
        >
          <div
            className="uppercase"
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '10px',
              letterSpacing: '0.24em',
              fontWeight: 500,
            }}
          >
            {anchor.label}
          </div>
          <div
            className="text-white"
            style={{
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              marginTop: '2px',
            }}
          >
            {anchor.value}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Orbiting image card — position/z/opacity fully driven by the parent.
// ─────────────────────────────────────────────────────────────
function OrbitingImageCard({
  card,
  fadeT,
  orbitOpacity,
  x,
  y,
  paintZ,
  scaleCue,
  isFront,
  hovered,
  selected,
  onHover,
  onClick,
}: {
  card: FloatCard;
  fadeT: number;
  orbitOpacity: number;
  x: number;
  y: number;
  paintZ: number;
  scaleCue: number;
  isFront: boolean;
  hovered: boolean;
  selected: boolean;
  onHover: (hoverIn: boolean) => void;
  onClick: () => void;
}) {
  const height = card.width * card.aspect;

  // Combine mount-fade with orbit-fade. If almost invisible, skip painting.
  const combinedOpacity = fadeT * orbitOpacity;
  if (combinedOpacity <= 0.005) return null;

  const hoverScale = hovered ? 1.05 : 1;

  return (
    <div
      className="absolute pointer-events-auto cursor-pointer"
      style={{
        left: `${x}px`,
        top: `${y}px`,
        width: `${card.width}px`,
        height: `${height}px`,
        transform: `translate(-50%, -50%) translateY(${(1 - fadeT) * 20}px) scale(${scaleCue * hoverScale})`,
        opacity: combinedOpacity,
        zIndex: paintZ,
        borderRadius: '10px',
        overflow: 'hidden',
        border: `1px solid rgba(122, 168, 238, ${selected ? 0.7 : 0.12})`,
        boxShadow:
          hovered || selected
            ? '0 20px 50px -8px rgba(0,0,0,0.7), 0 8px 20px -4px rgba(0,0,0,0.5), 0 0 22px 6px rgba(122,168,238,0.18)'
            : '0 14px 36px -8px rgba(0,0,0,0.6), 0 6px 14px -4px rgba(0,0,0,0.38)',
        transition:
          'opacity 200ms linear, box-shadow 240ms ease, border-color 240ms ease',
        willChange: 'transform, opacity, left, top',
      }}
      onPointerEnter={() => onHover(true)}
      onPointerLeave={() => onHover(false)}
      onClick={onClick}
    >
      <img
        src={card.src}
        alt=""
        className="w-full h-full object-cover object-top pointer-events-none"
        draggable={false}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 30%, rgba(0,0,0,0.15) 100%)',
        }}
      />
      {/* Slight tint on back-half cards — pushes them into the depth. */}
      {!isFront && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: 'rgba(20, 23, 31, 0.22)' }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Label displayed next to an image card while hovering the card
// ─────────────────────────────────────────────────────────────
function CardHoverLabel({
  x,
  y,
  anchor,
}: {
  x: number;
  y: number;
  anchor: AnchorLabel;
}) {
  const preferRight = x < window.innerWidth / 2 ? false : true;
  const showOnRight = !preferRight;
  const offset = 24;

  return (
    <div
      className="absolute"
      style={{
        left: `${x}px`,
        top: `${y}px`,
        pointerEvents: 'none',
        transform: 'translate(-50%, -50%)',
        zIndex: 26,
      }}
    >
      <div
        className="absolute whitespace-nowrap"
        style={{
          top: '-14px',
          left: showOnRight ? `${offset + 84}px` : undefined,
          right: showOnRight ? undefined : `${offset + 84}px`,
          textAlign: showOnRight ? 'left' : 'right',
          padding: '8px 12px',
          background: 'rgba(20, 23, 31, 0.82)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '4px',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          opacity: 1,
          animation: 'axiomLabelIn 240ms cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <div
          className="uppercase"
          style={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: '10px',
            letterSpacing: '0.24em',
            fontWeight: 500,
          }}
        >
          {anchor.label}
        </div>
        <div
          className="text-white"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '0.02em',
            marginTop: '2px',
          }}
        >
          {anchor.value}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Detail panel — fixed to the bottom-right when a card is clicked
// ─────────────────────────────────────────────────────────────
function CardDetailPanel({
  detail,
  onClose,
}: {
  detail: FloatCard['detail'];
  onClose: () => void;
}) {
  return (
    <div
      className="absolute pointer-events-auto"
      style={{
        right: '3vw',
        bottom: '13vh',
        width: '320px',
        padding: '18px 20px 18px 20px',
        background: 'rgba(20, 23, 31, 0.85)',
        border: '1px solid rgba(122, 168, 238, 0.22)',
        borderRadius: '8px',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow:
          '0 24px 60px -10px rgba(0,0,0,0.65), 0 0 24px 4px rgba(122,168,238,0.08)',
        zIndex: 27,
        animation: 'axiomDetailPanelIn 320ms cubic-bezier(0.22,1,0.36,1)',
      }}
    >
      <button
        onClick={onClose}
        className="absolute cursor-pointer flex items-center justify-center whitespace-nowrap"
        style={{
          top: '10px',
          right: '10px',
          width: '22px',
          height: '22px',
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: '4px',
          color: 'rgba(255,255,255,0.6)',
          fontSize: '13px',
          lineHeight: 1,
        }}
        aria-label="Close"
      >
        ×
      </button>

      <div
        className="uppercase"
        style={{
          color: '#7AA8EE',
          fontSize: '10px',
          letterSpacing: '0.28em',
          fontWeight: 500,
        }}
      >
        {detail.eyebrow}
      </div>

      <div
        className="text-white"
        style={{
          fontSize: '18px',
          fontWeight: 700,
          letterSpacing: '0.01em',
          marginTop: '6px',
          lineHeight: 1.25,
        }}
      >
        {detail.title}
      </div>

      <p
        style={{
          color: 'rgba(255,255,255,0.72)',
          fontSize: '13px',
          lineHeight: 1.6,
          marginTop: '10px',
        }}
      >
        {detail.body}
      </p>

      <div
        className="grid grid-cols-3 gap-3"
        style={{
          marginTop: '14px',
          paddingTop: '12px',
          borderTop: '1px solid rgba(255,255,255,0.10)',
        }}
      >
        {detail.spec.map((s) => (
          <div key={s.label} className="flex flex-col">
            <span
              className="uppercase"
              style={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: '9px',
                letterSpacing: '0.24em',
                fontWeight: 500,
              }}
            >
              {s.label}
            </span>
            <span
              className="text-white"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                marginTop: '3px',
              }}
            >
              {s.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Model z-index constant is documented above for reference; not exported.
void Z_MODEL;